import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import { toast } from 'sonner';
import { Send, Hash } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';

import { getBackendUrl } from '../utils/getBackendUrl';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Chat = ({ user }) => {
  const [socket, setSocket] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('general');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchProjects();
    initializeSocket();

    return () => {
      if (socket) {
        socket.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    if (socket && selectedRoom) {
      loadMessages();
      socket.emit('join_room', { room: selectedRoom });

      return () => {
        socket.emit('leave_room', { room: selectedRoom });
      };
    }
  }, [socket, selectedRoom]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const initializeSocket = () => {
    const newSocket = io(BACKEND_URL, {
      transports: ['websocket', 'polling']
    });

    newSocket.on('connect', () => {
      console.log('Socket connected');
    });

    newSocket.on('new_message', (message) => {
      setMessages((prev) => [...prev, message]);
    });

    setSocket(newSocket);
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      if (Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setProjects([]);
      }
    } catch (error) {
      console.error('Failed to fetch projects');
      setProjects([]);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages/${selectedRoom}`);
      if (Array.isArray(response.data)) {
        setMessages(response.data);
      } else {
        setMessages([]);
      }
    } catch (error) {
      console.error('Failed to load messages');
      setMessages([]);
    }
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !socket) return;

    socket.emit('send_message', {
      room_id: selectedRoom,
      user_id: user.id,
      user_name: user.name,
      message: newMessage
    });

    setNewMessage('');
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const rooms = [
    { id: 'general', name: 'General Chat' },
    ...projects.map(p => ({ id: `project-${p.id}`, name: p.name }))
  ];

  return (
    <div className="page-container" data-testid="chat-page">
      <h1 className="text-4xl font-bold text-white mb-6">Team Chat</h1>

      <div className="grid grid-cols-12 gap-6 h-[calc(100vh-200px)]">
        <div className="col-span-12 md:col-span-3 glass-card p-4">
          <h2 className="text-lg font-bold text-white mb-4">Rooms</h2>
          <div className="space-y-2">
            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => setSelectedRoom(room.id)}
                data-testid={`room-${room.id}`}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  selectedRoom === room.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-slate-800/50 text-slate-300 hover:bg-slate-800'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4" />
                  <span className="text-sm font-medium truncate">{room.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="col-span-12 md:col-span-9 glass-card flex flex-col">
          <div className="p-4 border-b border-slate-700">
            <h2 className="text-xl font-bold text-white">
              {rooms.find(r => r.id === selectedRoom)?.name}
            </h2>
          </div>

          <ScrollArea className="flex-1 p-4" data-testid="messages-container">
            <div className="space-y-4">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.user_id === user.id ? 'justify-end' : 'justify-start'}`}
                  data-testid={`message-${msg.id}`}
                >
                  <div
                    className={`max-w-[70%] p-3 rounded-lg ${
                      msg.user_id === user.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-800 text-slate-200'
                    }`}
                  >
                    {msg.user_id !== user.id && (
                      <p className="text-xs font-semibold mb-1 opacity-70">{msg.user_name}</p>
                    )}
                    <p className="text-sm">{msg.message}</p>
                    <p className="text-xs opacity-60 mt-1">
                      {new Date(msg.timestamp).toLocaleTimeString('en-CA', { timeZone: 'America/Toronto' })}
                    </p>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-700" data-testid="message-form">
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                data-testid="message-input"
                className="flex-1"
              />
              <Button
                type="submit"
                data-testid="send-message-btn"
                className="bg-gradient-to-r from-orange-500 to-orange-600"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Chat;